type NotificationLinkUser = {
  personaType?: string | null;
  isAdmin?: boolean;
} | null;

export type NotificationLinkSource = {
  eventKey?: string;
  metadata?: Record<string, unknown> | null;
};

const PROJECT_PREFIXES = [
  '/company/projects',
  '/person/projects',
  '/lawyer/projects',
  '/inspector/projects',
  '/admin/projects',
  '/projects'
];

const ADMIN_ACTION_PATHS = [
  /^\/admin\/(?:home|companies|company-invitations|profile|notifications|admin-list|accounting)$/,
  /^\/admin\/projects(?:\/[^/]+)?$/,
  /^\/admin\/(?:company-list|customers)(?:\/[^/]+)?$/,
  /^\/admin\/(?:lawyers|inspectors)(?:\/(?:view\/)?[^/]+)?$/,
  /^\/admin\/documents\/(?:library|agreements)$/,
  /^\/admin\/reports\/(?:contract-review|audit-trail|lawyer-requests|inspector-requests)$/,
  /^\/admin\/system\/(?:company-info|contract-config|project-config|kpi-defaults|debug)$/,
  /^\/admin\/debug$/,
  /^\/admin\/transactions(?:\/[^/]+)?$/,
  /^\/admin\/adyen-(?:ledger|accounts|webhook-logs)$/
];

const hasControlCharacter = (value: string) =>
  Array.from(value).some(character => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 31 || codePoint === 127;
  });

const isInternalPath = (value: string) =>
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.includes('\\') &&
  !hasControlCharacter(value);

const isAdminUser = (user: NotificationLinkUser) =>
  Boolean(user?.isAdmin || user?.personaType === 'admin');

const stringMetadata = (
  metadata: Record<string, unknown>,
  key: string
) => {
  const value = metadata[key];
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : null;
};

export const projectBasePath = (user: NotificationLinkUser) => {
  if (isAdminUser(user)) return '/admin/projects';
  if (user?.personaType === 'person') return '/person/projects';
  if (user?.personaType === 'lawyer') return '/lawyer/projects';
  if (user?.personaType === 'inspector') return '/inspector/projects';
  return '/projects';
};

type ProjectNotificationTab =
  | 'timeline'
  | 'transactions'
  | 'contract'
  | 'documents';

const MILESTONE_REPLY_EVENTS = new Set([
  'milestone.rejected',
  'milestone.message_posted',
  'milestone.progressed',
  'milestone.conflict_started',
  'milestone.dispute_started'
]);

const timelineQuery = (
  eventKey: string,
  metadata: Record<string, unknown>
) => {
  const fundingContractId = stringMetadata(metadata, 'contractId');
  if (['payment.funding_required', 'payment.funding_reminder'].includes(eventKey) && fundingContractId) {
    return `?tab=timeline&paymentPicker=open&paymentContractId=${encodeURIComponent(fundingContractId)}`;
  }
  const milestoneId = stringMetadata(metadata, 'milestoneId');
  // The landing rule (ClickUp 869fnf0e5): while the stage waits for the
  // reader's attestation, a stage notification opens the attestation.
  if (milestoneId && eventKey.startsWith('milestone.') && metadata.attestationLanding === true) {
    return `?tab=timeline&milestoneId=${encodeURIComponent(milestoneId)}&action=attest`;
  }
  if (!milestoneId || !MILESTONE_REPLY_EVENTS.has(eventKey)) {
    return '?tab=timeline';
  }
  return `?tab=timeline&milestoneId=${encodeURIComponent(milestoneId)}&action=reply`;
};

const CONTRACT_WORKSPACE_EVENTS = new Set([
  'contract.admin_unlocked',
  'contract.created',
  'contract.draft_approved',
  'contract.draft_rejected',
  'contract.draft_sent',
  'contract.ready_to_sign',
  'contract.signature_reminder',
  'contract.signed_by_company'
]);

// Notifications that ask the person to review or sign: they open "Granska och
// signera avtal" directly (review=1) instead of only selecting the contract.
const PERSON_REVIEW_EVENTS = new Set([
  'contract.draft_sent',
  'contract.ready_to_sign',
  'contract.signature_reminder',
  'contract.signed_by_company'
]);

const contractTabQuery = (
  contractNumber: string | null,
  eventKey: string,
  user: NotificationLinkUser
) =>
  `?tab=contract${
    contractNumber ? `&contractNumber=${encodeURIComponent(contractNumber)}` : ''
  }${user?.personaType === 'person' && PERSON_REVIEW_EVENTS.has(eventKey) ? '&review=1' : ''}`;

const TRANSACTION_WORKSPACE_EVENTS = new Set([
  'payment.completed',
  'payment.held',
  'payment.initiated',
  'payment.received',
  'payout.booked',
  'payout.completed'
]);

const projectNotificationTab = (
  eventKey: string
): ProjectNotificationTab | null => {
  if (eventKey.startsWith('document.')) return 'documents';
  if (CONTRACT_WORKSPACE_EVENTS.has(eventKey)) return 'contract';
  if (TRANSACTION_WORKSPACE_EVENTS.has(eventKey)) return 'transactions';
  if (
    [
      'contract.completed',
      'contract.signed',
      'contract.signed_by_both',
      'contract.signed_by_person'
    ].includes(eventKey) ||
    eventKey.startsWith('milestone.') ||
    eventKey.startsWith('payment.') ||
    eventKey.startsWith('payment_interest.') ||
    eventKey.startsWith('inspection.') ||
    eventKey === 'inspection_date_confirmed' ||
    eventKey.startsWith('project.')
  ) {
    return 'timeline';
  }
  return null;
};

const roleAdjustedProjectPath = (
  actionPath: string,
  user: NotificationLinkUser
) => {
  const prefix = PROJECT_PREFIXES.find(candidate =>
    actionPath === candidate || actionPath.startsWith(`${candidate}/`)
  );
  if (!prefix) return actionPath;
  return `${projectBasePath(user)}${actionPath.slice(prefix.length)}`;
};

const projectActionPath = (
  eventKey: string,
  metadata: Record<string, unknown>,
  user: NotificationLinkUser
) => {
  const projectId = stringMetadata(metadata, 'projectId');
  if (!projectId) {
    return eventKey === 'document.uploaded'
      ? isAdminUser(user)
        ? '/admin/documents/library'
        : '/home'
      : null;
  }
  const basePath = `${projectBasePath(user)}/${encodeURIComponent(projectId)}`;
  const destinationTab = projectNotificationTab(eventKey);
  if (destinationTab === 'documents') return `${basePath}?tab=documents`;
  if (destinationTab === 'timeline') {
    return `${basePath}${timelineQuery(eventKey, metadata)}`;
  }
  if (destinationTab === 'contract') {
    return `${basePath}${contractTabQuery(stringMetadata(metadata, 'contractNumber'), eventKey, user)}`;
  }
  return basePath;
};

const derivedActionPath = (
  notification: NotificationLinkSource,
  user: NotificationLinkUser
) => {
  const eventKey = notification.eventKey ?? '';
  const metadata = notification.metadata ?? {};
  const invitationId = stringMetadata(metadata, 'invitationId');

  if (eventKey === 'user.welcome') return '/onboarding';
  if (eventKey === 'company.invitation.sent' || eventKey === 'company.linked') return '/my-companies';
  if (
    invitationId &&
    (eventKey === 'invitation.company_invited' ||
      eventKey === 'invitation.customer_invited')
  ) {
    return `/invitations/${encodeURIComponent(invitationId)}/decision`;
  }
  // An invitation waits on the start page (ClickUp 869fmx898).
  if (eventKey === 'invitation.open') return '/home';
  if (eventKey === 'invitation.accepted' || eventKey === 'invitation.declined') {
    return user?.personaType === 'person' ? '/my-companies' : '/customers';
  }
  if (eventKey === 'customer.verified' || eventKey === 'customer.linked') {
    const customerId = stringMetadata(metadata, 'customerId');
    return customerId ? `/customers/${encodeURIComponent(customerId)}` : '/customers';
  }
  if (eventKey === 'payout.reversed' && !isAdminUser(user)) {
    return '/settings/company?tab=adyen';
  }
  if (eventKey === 'adyen.unknown_transfer_webhook') {
    return isAdminUser(user) ? '/admin/adyen-webhook-logs' : null;
  }
  if (eventKey.startsWith('adyen.payment_lifecycle.')) {
    const projectId = stringMetadata(metadata, 'projectId');
    return isAdminUser(user) && projectId
      ? `/admin/transactions?projectId=${encodeURIComponent(projectId)}`
      : isAdminUser(user)
        ? '/admin/adyen-webhook-logs'
        : null;
  }
  return projectActionPath(eventKey, metadata, user);
};

const isKnownActionPath = (path: string, user: NotificationLinkUser) => {
  const pathname = path.split(/[?#]/, 1)[0];
  if (pathname.startsWith('/admin/')) {
    return isAdminUser(user) && ADMIN_ACTION_PATHS.some(pattern => pattern.test(pathname));
  }
  return [
    /^\/home$/,
    /^\/onboarding$/,
    /^\/profile$/,
    /^\/notifications$/,
    // A message notification opens its thread on Meddelanden (ClickUp 869fegbvk).
    /^\/messages$/,
    /^\/my-(companies|lawyers|inspectors)$/,
    /^\/marketplace$/,
    /^\/invite(?:-status)?$/,
    /^\/invitations\/[^/]+\/decision$/,
    /^\/customers(?:\/[^/]+)?$/,
    /^\/(?:person\/|lawyer\/|inspector\/)?projects(?:\/[^/]+)?$/,
    /^\/contracts\/[^/]+\/signing$/,
    /^\/settings\/(?:company|lawyer|inspector)(?:\/marketplace-preview)?$/,
    /^\/payment-checkout$/,
    /^\/accounting\/company-report$/
  ].some(pattern => pattern.test(pathname));
};

export const notificationActionPath = (
  notification: NotificationLinkSource,
  user: NotificationLinkUser
): string | null => {
  const metadata = notification.metadata ?? {};
  const rawActionUrl = metadata.actionUrl;
  const derived = derivedActionPath(notification, user);

  if (typeof rawActionUrl !== 'string' || !isInternalPath(rawActionUrl)) {
    return derived;
  }
  if (rawActionUrl === '/') return derived;

  let normalized = rawActionUrl;
  if (normalized === '/person/companies') normalized = '/my-companies';
  if (normalized === '/invite/status') normalized = '/invite-status';
  if (normalized.startsWith('/company/projects/')) {
    normalized = normalized.replace('/company/projects/', '/projects/');
  }
  normalized = roleAdjustedProjectPath(normalized, user);

  const destinationTab = projectNotificationTab(notification.eventKey ?? '');
  const projectPath = normalized.split(/[?#]/, 1)[0];
  if (
    destinationTab &&
    /^\/(?:admin\/|person\/|lawyer\/|inspector\/)?projects\/[^/]+$/.test(projectPath)
  ) {
    if (destinationTab === 'contract') {
      const query = normalized.includes('?')
        ? normalized.slice(normalized.indexOf('?') + 1).split('#', 1)[0]
        : '';
      const contractNumber =
        stringMetadata(metadata, 'contractNumber') ??
        new URLSearchParams(query).get('contractNumber');
      return `${projectPath}${contractTabQuery(contractNumber, notification.eventKey ?? '', user)}`;
    }
    return destinationTab === 'timeline'
      ? `${projectPath}${timelineQuery(notification.eventKey ?? '', metadata)}`
      : `${projectPath}?tab=${destinationTab}`;
  }

  if (
    user?.personaType === 'person' &&
    (notification.eventKey === 'invitation.accepted' ||
      notification.eventKey === 'invitation.declined') &&
    normalized === '/customers'
  ) {
    normalized = '/my-companies';
  }

  return isKnownActionPath(normalized, user) ? normalized : derived;
};
